// src/exportUtils.js
// Client-side file generation for Chat History / Sources / Prompts export.
// No server calls -- everything happens in the browser.

import jsPDF from 'jspdf';
import { Document, Packer, Paragraph, TextRun } from 'docx';

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadAsMarkdown(text, filename) {
  const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
  triggerDownload(blob, filename.endsWith('.md') ? filename : `${filename}.md`);
}

export function downloadAsText(text, filename) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  triggerDownload(blob, filename.endsWith('.txt') ? filename : `${filename}.txt`);
}

export function downloadAsPDF(text, filename, title) {
  const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
  const marginX = 40;
  const marginTop = 50;
  const marginBottom = 50;
  const pageHeight = pdf.internal.pageSize.getHeight();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const usableWidth = pageWidth - marginX * 2;
  const lineHeight = 14;

  let y = marginTop;

  pdf.setFontSize(14);
  pdf.text(title, marginX, y);
  y += lineHeight * 2;

  pdf.setFontSize(10);
  const paragraphs = text.split('\n');
  paragraphs.forEach(paragraph => {
    const wrapped = pdf.splitTextToSize(paragraph.length ? paragraph : ' ', usableWidth);
    wrapped.forEach(line => {
      if (y > pageHeight - marginBottom) {
        pdf.addPage();
        y = marginTop;
      }
      pdf.text(line, marginX, y);
      y += lineHeight;
    });
  });

  pdf.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
}

export async function downloadAsDocx(text, filename, title) {
  const titleParagraph = new Paragraph({
    children: [new TextRun({ text: title, bold: true, size: 32 })],
    spacing: { after: 300 },
  });

  const bodyParagraphs = text.split('\n').map(line =>
    new Paragraph({ children: [new TextRun(line.length ? line : ' ')], spacing: { after: 120 } })
  );

  const doc = new Document({
    sections: [{ children: [titleParagraph, ...bodyParagraphs] }],
  });

  const blob = await Packer.toBlob(doc);
  triggerDownload(blob, filename.endsWith('.docx') ? filename : `${filename}.docx`);
}

export function exportContent(format, text, filename, title) {
  if (format === 'md') return downloadAsMarkdown(text, filename);
  if (format === 'txt') return downloadAsText(text, filename);
  if (format === 'pdf') return downloadAsPDF(text, filename, title);
  if (format === 'docx') return downloadAsDocx(text, filename, title);
  throw new Error(`Unknown export format: ${format}`);
}
